import { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Chip,
  Button,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Select,
  SelectItem,
  Skeleton,
  Switch,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { supabase } from 'common/src/supabase';
import { getAllUsers } from 'common/src/api/users';
import { Exporter, User } from 'common/src/utils/types';
import { format } from 'date-fns';
import { SEASONS, CURRENT_SEASON } from 'common/src/utils/definitions';
import { exporterUsesFrutic } from 'common/src/utils/frutic-functions';
import Header from '../../../components/layouts/Header';
import { PageLayout, StatusChip } from '../../../components/ui';
import { announcementStatus } from '../../../announcements/announcements';

type TimeWindow = 7 | 14 | 30 | 90;

/** Programa Comercial de una exportadora en la temporada, por especie. */
interface ProgramSummary {
  species: string;
  commitments: number;
  clients: number;
  createdAt: string;
  lastEditAt: string | null;
}

type ProgramStatus = 'loaded' | 'empty' | 'none';

const programStatus = (programs: ProgramSummary[]): ProgramStatus =>
  programs.length === 0
    ? 'none'
    : programs.some((p) => p.commitments > 0)
      ? 'loaded'
      : 'empty';

// PostgREST devuelve de a 1000 filas: se pide por páginas.
const fetchAllPages = async <T,>(
  query: (from: number, to: number) => PromiseLike<{ data: T[] | null }>
) => {
  const rows: T[] = [];
  for (let from = 0; ; from += 1000) {
    const { data } = await query(from, from + 999);
    rows.push(...(data || []));
    if (!data || data.length < 1000) return rows;
  }
};

interface ActionRow {
  created_at: string;
  entity_id: string | null;
  users: {
    email: string;
    exporter: { id: number; name: string } | null;
  };
}

interface ExporterUsage {
  exporter: Pick<Exporter, 'id' | 'name' | 'hasFrutic' | 'hasSettlements'> & {
    isActive?: boolean;
  };
  visitedDailyMarket: boolean;
  visitedDashboard: boolean;
  visitedSettlementsSummary: boolean;
  hasShipmentBoxes: boolean | null; // null = N/A (Frutic)
  hasSettlementsUploaded: boolean;
  lastShipmentsUploadAt: string | null;
  lastSettlementsUploadAt: string | null;
  lastVisitedDailyMarketAt: string | null;
  lastVisitedDashboardAt: string | null;
  lastVisitedSettlementsSummaryAt: string | null;
  lastActivityAt: string | null;
  usersActiveCount: number;
  users: User[];
  userStats: Record<
    string,
    {
      lastActivityAt: string | null;
      visitedDailyMarket: boolean;
      visitedDashboard: boolean;
      visitedSettlementsSummary: boolean;
    }
  >; // by email
}

const SuperadminClients = () => {
  const [timeWindow, setTimeWindow] = useState<TimeWindow>(30);
  const [season, setSeason] = useState<string>(CURRENT_SEASON);
  const [exporters, setExporters] = useState<Exporter[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [actions, setActions] = useState<ActionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [selectedExporterId, setSelectedExporterId] = useState<number | null>(
    null
  );
  const [userSearch, setUserSearch] = useState<string>('');
  useEffect(() => {
    if (selectedExporterId !== null) {
      setUserSearch('');
    }
  }, [selectedExporterId]);
  const [showInactiveOnly, setShowInactiveOnly] = useState(false);
  const [showOnlyActive, setShowOnlyActive] = useState(true);
  const [exporterSearch, setExporterSearch] = useState<string>('');
  const [showWithoutProgramOnly, setShowWithoutProgramOnly] = useState(false);
  const [programsByExporter, setProgramsByExporter] = useState<
    Map<number, ProgramSummary[]>
  >(new Map());

  // Programa Comercial de cada exportadora en la temporada elegida.
  useEffect(() => {
    let active = true;
    const fetchPrograms = async () => {
      const { data: programs } = await supabase
        .from('commercial_programs')
        .select('id, exporter_id, created_at, species:species_id(name)')
        .eq('season', season);
      const ids = (programs || []).map((p) => p.id);
      const rows = ids.length
        ? await fetchAllPages<{
            program_id: number;
            exporter_receiver_id: number;
            quantity: number | null;
            created_at: string;
            deleted_at: string | null;
          }>((from, to) =>
            supabase
              .from('commitments')
              .select(
                'program_id, exporter_receiver_id, quantity, created_at, deleted_at'
              )
              .in('program_id', ids)
              .order('id')
              .range(from, to)
          )
        : [];
      if (!active) return;

      const byExporter = new Map<number, ProgramSummary[]>();
      (programs || []).forEach((program) => {
        const programRows = rows.filter((r) => r.program_id === program.id);
        const current = programRows.filter(
          (r) => !r.deleted_at && Number(r.quantity) > 0
        );
        // Último cambio: una fila nueva o una que se reemplazó.
        const lastEditAt =
          programRows
            .flatMap((r) => [r.created_at, r.deleted_at])
            .filter((d): d is string => !!d)
            .sort()
            .pop() ?? null;
        const species = program.species as unknown as { name: string } | null;
        byExporter.set(program.exporter_id, [
          ...(byExporter.get(program.exporter_id) || []),
          {
            species: species?.name ?? '',
            commitments: current.length,
            clients: new Set(current.map((r) => r.exporter_receiver_id)).size,
            createdAt: program.created_at,
            lastEditAt,
          },
        ]);
      });
      setProgramsByExporter(byExporter);
    };
    fetchPrograms();
    return () => {
      active = false;
    };
  }, [season]);

  useEffect(() => {
    const fetchBase = async () => {
      setLoading(true);
      try {
        // Exporters
        const { data: exportersData } = await supabase
          .from('exporters')
          .select('*')
          .order('name');
        setExporters((exportersData || []) as Exporter[]);

        // Users
        const users = (await getAllUsers()) as User[];
        setAllUsers(users);
      } finally {
        setLoading(false);
      }
    };
    fetchBase();
  }, []);

  useEffect(() => {
    const fetchActions = async () => {
      const sinceIso = new Date(
        Date.now() - timeWindow * 24 * 60 * 60 * 1000
      ).toISOString();

      const { data, error } = await supabase
        .from('actions')
        .select(
          `created_at, entity_id, users!inner(email, exporter:exporters(id, name))`
        )
        .gte('created_at', sinceIso)
        .order('created_at', { ascending: false });

      if (!error) setActions((data || []) as unknown as ActionRow[]);
    };
    fetchActions();
  }, [timeWindow]);

  const usageByExporter = useMemo<ExporterUsage[]>(() => {
    if (exporters.length === 0) return [];

    // Build a map of actions per exporter
    const actionsByExporter = new Map<number, ActionRow[]>();
    actions.forEach((a) => {
      const exporterId = a.users?.exporter?.id;
      if (!exporterId) return;
      if (!actionsByExporter.has(exporterId))
        actionsByExporter.set(exporterId, []);
      actionsByExporter.get(exporterId)!.push(a);
    });

    // Precompute users per exporter
    const usersByExporter = new Map<number, User[]>();
    allUsers.forEach((u) => {
      const expId = u.exporter_id || u.exporter?.id;
      if (!expId) return;
      if (!usersByExporter.has(expId)) usersByExporter.set(expId, []);
      usersByExporter.get(expId)!.push(u);
    });

    return exporters.map((exp) => {
      const expActions = actionsByExporter.get(exp.id) || [];
      const users = usersByExporter.get(exp.id) || [];

      const visitedDailyMarket = expActions.some(
        (a) => a.entity_id === 'daily-market'
      );
      const visitedDashboard = expActions.some(
        (a) => a.entity_id === 'dashboard'
      );
      const visitedSettlementsSummary = expActions.some(
        (a) => a.entity_id === 'settlements-summary'
      );

      const lastVisitedDailyMarketAt = (() => {
        const items = expActions.filter((a) => a.entity_id === 'daily-market');
        return items.length ? items[0].created_at : null;
      })();
      const lastVisitedDashboardAt = (() => {
        const items = expActions.filter((a) => a.entity_id === 'dashboard');
        return items.length ? items[0].created_at : null;
      })();
      const lastVisitedSettlementsSummaryAt = (() => {
        const items = expActions.filter(
          (a) => a.entity_id === 'settlements-summary'
        );
        return items.length ? items[0].created_at : null;
      })();
      const lastActivityAt =
        expActions.length > 0 ? expActions[0].created_at : null;
      const usersActive = new Set(
        expActions.map((a) => a.users?.email).filter(Boolean) as string[]
      );

      // User-level stats
      const userStats: ExporterUsage['userStats'] = {};
      users.forEach((u) => {
        const email = u.email;
        const acts = expActions.filter((a) => a.users?.email === email);
        userStats[email] = {
          lastActivityAt: acts.length > 0 ? acts[0].created_at : null,
          visitedDailyMarket: acts.some((a) => a.entity_id === 'daily-market'),
          visitedDashboard: acts.some((a) => a.entity_id === 'dashboard'),
          visitedSettlementsSummary: acts.some(
            (a) => a.entity_id === 'settlements-summary'
          ),
        };
      });

      return {
        exporter: {
          id: exp.id,
          name: exp.name,
          hasFrutic: exporterUsesFrutic(exp, season),
          hasSettlements: (exp as any).hasSettlements,
          isActive: (exp as any).isActive,
        },
        visitedDailyMarket,
        visitedDashboard,
        hasShipmentBoxes: null, // fill later
        hasSettlementsUploaded: false, // fill later
        visitedSettlementsSummary,
        lastShipmentsUploadAt: null,
        lastSettlementsUploadAt: null,
        lastVisitedDailyMarketAt,
        lastVisitedDashboardAt,
        lastVisitedSettlementsSummaryAt,
        lastActivityAt,
        usersActiveCount: usersActive.size,
        users,
        userStats,
      } as ExporterUsage;
    });
  }, [exporters, actions, allUsers, season]);

  // Enrich with shipment boxes and settlements presence
  const [finalUsage, setFinalUsage] = useState<ExporterUsage[]>([]);
  useEffect(() => {
    const enrich = async () => {
      if (usageByExporter.length === 0) {
        setFinalUsage([]);
        return;
      }

      const expIds = usageByExporter.map((u) => u.exporter.id);

      // Shipments: fetch rows for these exporters and selected season
      const { data: sbRows } = await supabase
        .from('shipments')
        .select('exporter_id, created_at')
        .in('exporter_id', expIds)
        .eq('season', season);
      const sbLatestByExporter = new Map<number, string>();
      (sbRows || []).forEach((r: any) => {
        const prev = sbLatestByExporter.get(r.exporter_id);
        const curr = r.created_at;
        if (!prev || (curr && new Date(curr) > new Date(prev))) {
          if (curr) sbLatestByExporter.set(r.exporter_id, curr);
        }
      });
      const sbSet = new Set(Array.from(sbLatestByExporter.keys()));

      // Settlements: fetch rows for these exporters and selected season
      const { data: stRows } = await supabase
        .from('settlements')
        .select('exporter_id, created_at')
        .in('exporter_id', expIds)
        .eq('season', season);
      const stLatestByExporter = new Map<number, string>();
      (stRows || []).forEach((r: any) => {
        const prev = stLatestByExporter.get(r.exporter_id);
        const curr = r.created_at;
        if (!prev || (curr && new Date(curr) > new Date(prev))) {
          if (curr) stLatestByExporter.set(r.exporter_id, curr);
        }
      });
      const stSet = new Set(Array.from(stLatestByExporter.keys()));

      setFinalUsage(
        usageByExporter.map((u) => ({
          ...u,
          hasShipmentBoxes: u.exporter.hasFrutic
            ? null
            : sbSet.has(u.exporter.id),
          hasSettlementsUploaded: stSet.has(u.exporter.id),
          lastShipmentsUploadAt: sbLatestByExporter.get(u.exporter.id) || null,
          lastSettlementsUploadAt:
            stLatestByExporter.get(u.exporter.id) || null,
        }))
      );
    };
    enrich();
  }, [usageByExporter, season]);

  const toggleExpand = (exporterId: number) => {
    setExpanded((prev) => ({ ...prev, [exporterId]: !prev[exporterId] }));
  };

  const statusChip = (
    ok: boolean | null,
    labelOk: string,
    labelNa?: string
  ) => {
    if (ok === null)
      return (
        <Chip size='sm' variant='flat' color='warning'>
          {labelNa || 'N/A'}
        </Chip>
      );
    return ok ? (
      <Chip size='sm' variant='flat' color='success'>
        {labelOk}
      </Chip>
    ) : (
      <Chip size='sm' variant='flat' color='default'>
        No
      </Chip>
    );
  };

  const formatSince = (iso: string | null) => {
    if (!iso) return '-';
    const d = new Date(iso);
    const now = new Date();
    const ms = now.getTime() - d.getTime();
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    if (days <= 0) return 'hoy';
    if (days < 7) return `hace ${days} día${days === 1 ? '' : 's'}`;
    const weeks = Math.floor(days / 7);
    if (weeks < 8) return `hace ${weeks} semana${weeks === 1 ? '' : 's'}`;
    const months = Math.floor(days / 30);
    return `hace ${months} mes${months === 1 ? '' : 'es'}`;
  };

  const programCell = (programs: ProgramSummary[]) => {
    const status = programStatus(programs);
    return (
      <div className='flex flex-col items-start gap-1'>
        <StatusChip
          tone={
            status === 'loaded'
              ? 'success'
              : status === 'empty'
                ? 'warning'
                : 'default'
          }
        >
          {status === 'loaded'
            ? 'Cargado'
            : status === 'empty'
              ? 'Sin compromisos'
              : 'No'}
        </StatusChip>
        {programs.map((p) => (
          <span key={p.species} className='text-xs text-default-500'>
            {programs.length > 1 && `${p.species}: `}
            {p.commitments > 0
              ? `${p.clients} cliente${p.clients === 1 ? '' : 's'} · ${p.commitments} compromisos · editado ${formatSince(p.lastEditAt)}`
              : `creado ${formatSince(p.createdAt)}`}
          </span>
        ))}
      </div>
    );
  };

  return (
    <PageLayout>
      <Header title='Clientes - Uso de Plataforma' />

      <div className='mb-4 flex gap-3 items-end flex-wrap'>
        <div className='w-56'>
          <Select
            label='Temporada'
            selectedKeys={[season]}
            onSelectionChange={(keys) =>
              setSeason(Array.from(keys)[0] as string)
            }
          >
            {Object.values(SEASONS).map((s) => (
              <SelectItem key={s.name}>{s.name}</SelectItem>
            ))}
          </Select>
        </div>
        <div className='w-44'>
          <Select
            label='Ventana de tiempo'
            selectedKeys={[timeWindow.toString()]}
            onSelectionChange={(keys) =>
              setTimeWindow(
                parseInt(Array.from(keys)[0] as string) as TimeWindow
              )
            }
          >
            <SelectItem key='7'>Últimos 7 días</SelectItem>
            <SelectItem key='14'>Últimos 14 días</SelectItem>
            <SelectItem key='30'>Últimos 30 días</SelectItem>
            <SelectItem key='90'>Últimos 90 días</SelectItem>
          </Select>
        </div>
        <div className='w-64'>
          <Input
            size='sm'
            label='Buscar exportadora'
            placeholder='nombre...'
            value={exporterSearch}
            onValueChange={setExporterSearch}
          />
        </div>
        <div className='flex items-center gap-2'>
          <Switch
            size='sm'
            isSelected={showInactiveOnly}
            onValueChange={setShowInactiveOnly}
          >
            Solo inactivos
          </Switch>
        </div>
        <div className='flex items-center gap-2'>
          <Switch
            size='sm'
            isSelected={showOnlyActive}
            onValueChange={setShowOnlyActive}
          >
            Solo activos
          </Switch>
        </div>
        <div className='flex items-center gap-2'>
          <Switch
            size='sm'
            isSelected={showWithoutProgramOnly}
            onValueChange={setShowWithoutProgramOnly}
          >
            Sin programa comercial
          </Switch>
        </div>
      </div>

      <Card>
        <CardBody>
          {loading ? (
            <Skeleton className='w-full h-24' />
          ) : (
            <Table removeWrapper aria-label='Client usage summary'>
              <TableHeader>
                <TableColumn>EXPORTADORA</TableColumn>
                <TableColumn>DAILY MARKET</TableColumn>
                <TableColumn>DASHBOARD</TableColumn>
                <TableColumn>LIQ (VISITA)</TableColumn>
                <TableColumn>CARGA DESPACHOS</TableColumn>
                <TableColumn>CARGA LIQUIDACIONES</TableColumn>
                <TableColumn>PROGRAMA COMERCIAL</TableColumn>
                <TableColumn>USUARIOS ACTIVOS</TableColumn>
                <TableColumn>ÚLTIMA ACTIVIDAD</TableColumn>
              </TableHeader>
              <TableBody emptyContent='No hay datos'>
                {finalUsage
                  .filter((u) => {
                    if (showOnlyActive && u.exporter.isActive === false)
                      return false;
                    return true;
                  })
                  .filter((u) => {
                    if (!showInactiveOnly) return true;
                    const noVisits =
                      !u.visitedDailyMarket &&
                      !u.visitedDashboard &&
                      !u.visitedSettlementsSummary;
                    const noUploads =
                      (u.exporter.hasFrutic || !u.hasShipmentBoxes) &&
                      !u.hasSettlementsUploaded;
                    return noVisits && noUploads;
                  })
                  .filter(
                    (u) =>
                      !showWithoutProgramOnly ||
                      programStatus(
                        programsByExporter.get(u.exporter.id) || []
                      ) !== 'loaded'
                  )
                  .filter((u) =>
                    exporterSearch
                      ? u.exporter.name
                          .toLowerCase()
                          .includes(exporterSearch.toLowerCase())
                      : true
                  )
                  .map((u) => (
                    <TableRow
                      key={u.exporter.id}
                      className='cursor-pointer hover:bg-default-100'
                      onClick={() => setSelectedExporterId(u.exporter.id)}
                    >
                      <TableCell>{u.exporter.name}</TableCell>
                      <TableCell>
                        <div className='flex flex-col gap-1'>
                          {statusChip(u.visitedDailyMarket, 'Sí')}
                          <span className='text-xs text-default-500'>
                            {formatSince(u.lastVisitedDailyMarketAt)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className='flex flex-col gap-1'>
                          {statusChip(u.visitedDashboard, 'Sí')}
                          <span className='text-xs text-default-500'>
                            {formatSince(u.lastVisitedDashboardAt)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className='flex flex-col gap-1'>
                          {statusChip(u.visitedSettlementsSummary, 'Sí')}
                          <span className='text-xs text-default-500'>
                            {formatSince(u.lastVisitedSettlementsSummaryAt)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className='flex flex-col gap-1'>
                          {statusChip(
                            u.hasShipmentBoxes,
                            'Sí',
                            u.exporter.hasFrutic ? 'Frutic' : 'N/A'
                          )}
                          <span className='text-xs text-default-500'>
                            {u.exporter.hasFrutic
                              ? 'Integrado via Frutic'
                              : formatSince(u.lastShipmentsUploadAt)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className='flex flex-col gap-1'>
                          {statusChip(u.hasSettlementsUploaded, 'Sí')}
                          <span className='text-xs text-default-500'>
                            {formatSince(u.lastSettlementsUploadAt)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {programCell(
                          programsByExporter.get(u.exporter.id) || []
                        )}
                      </TableCell>
                      <TableCell>{u.usersActiveCount}</TableCell>
                      <TableCell>
                        {u.lastActivityAt
                          ? format(
                              new Date(u.lastActivityAt),
                              'dd/MM/yyyy HH:mm'
                            )
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Usuarios de la exportadora: drawer a la derecha, como el de la Bitácora de Liquidación. */}
      <Modal
        isOpen={selectedExporterId !== null}
        onClose={() => setSelectedExporterId(null)}
        hideCloseButton
        classNames={{
          wrapper: '!items-stretch !justify-end',
          base: '!m-0 h-[100dvh] !rounded-l-large !rounded-r-none !w-full sm:!max-w-[700px]',
        }}
      >
        <ModalContent className='flex h-full flex-col'>
          {(closeDrawer) => (
            <>
              <ModalHeader className='flex items-start justify-between gap-2 border-b-1 border-default-100'>
                <div className='flex flex-col gap-1'>
                  <span className='font-semibold'>
                    {finalUsage.find(
                      (u) => u.exporter.id === selectedExporterId
                    )?.exporter.name || 'Exportadora'}
                  </span>
                  <span className='text-xs font-normal text-default-500'>
                    Ventana: últimos {timeWindow} días — Temporada: {season}
                  </span>
                </div>
                <Button
                  isIconOnly
                  size='sm'
                  variant='light'
                  aria-label='Cerrar'
                  onPress={closeDrawer}
                >
                  <Icon icon='tabler:x' width={18} />
                </Button>
              </ModalHeader>
              <ModalBody className='flex-grow gap-4 overflow-y-auto py-4'>
                <Input
                  size='sm'
                  label='Buscar usuario'
                  placeholder='email...'
                  value={userSearch}
                  onValueChange={setUserSearch}
                />
                <Table aria-label='User details' removeWrapper isCompact>
                  <TableHeader>
                    <TableColumn>USUARIO</TableColumn>
                    <TableColumn>ÚLTIMA ACTIVIDAD</TableColumn>
                    <TableColumn>DAILY</TableColumn>
                    <TableColumn>DASH</TableColumn>
                    <TableColumn>LIQ (VISITA)</TableColumn>
                    <TableColumn>AVISO P. COMERCIAL</TableColumn>
                  </TableHeader>
                  <TableBody emptyContent='Sin usuarios'>
                    {finalUsage
                      .find((u) => u.exporter.id === selectedExporterId)
                      ?.users.filter((usr) =>
                        userSearch
                          ? usr.email
                              .toLowerCase()
                              .includes(userSearch.toLowerCase())
                          : true
                      )
                      .map((usr) => {
                        const usage = finalUsage.find(
                          (u) => u.exporter.id === selectedExporterId
                        );
                        const stats = usage?.userStats[usr.email] || {
                          lastActivityAt: null,
                          visitedDailyMarket: false,
                          visitedDashboard: false,
                          visitedSettlementsSummary: false,
                        };
                        return (
                          <TableRow key={usr.id}>
                            <TableCell>{usr.email}</TableCell>
                            <TableCell>
                              {stats.lastActivityAt
                                ? format(
                                    new Date(stats.lastActivityAt),
                                    'dd/MM/yyyy HH:mm'
                                  )
                                : '-'}
                            </TableCell>
                            <TableCell>
                              {statusChip(stats.visitedDailyMarket, 'Sí')}
                            </TableCell>
                            <TableCell>
                              {statusChip(stats.visitedDashboard, 'Sí')}
                            </TableCell>
                            <TableCell>
                              {statusChip(
                                stats.visitedSettlementsSummary,
                                'Sí'
                              )}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const status = announcementStatus(
                                  usr,
                                  'commercial-program'
                                );
                                return (
                                  <StatusChip
                                    tone={
                                      status === 'seen'
                                        ? 'success'
                                        : status === 'pending'
                                          ? 'warning'
                                          : 'default'
                                    }
                                  >
                                    {status === 'seen'
                                      ? 'Visto'
                                      : status === 'pending'
                                        ? 'Pendiente'
                                        : 'No aplica'}
                                  </StatusChip>
                                );
                              })()}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </PageLayout>
  );
};

export default SuperadminClients;
